import { supabase } from '@/integrations/supabase/client';

export interface CourseProgressSummary {
  totalModules: number;
  completedModules: number;
  overallProgress: number;
  isCompleted: boolean;
}

/**
 * Unified Progress Service - Single Source of Truth
 * All progress tracking goes through this service
 */
export class UnifiedProgressService {
  
  /**
   * Initialize progress for a module (0% status)
   * Called when user first accesses a module
   */
  static async initializeModuleProgress(
    userId: string,
    moduleId: string,
    courseId: string
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_module_progress')
        .insert({
          user_id: userId,
          module_id: moduleId,
          course_id: courseId,
          progress: 0
        })
        .select()
        .single();
      
      if (error && error.code !== '23505') { // Ignore duplicate key errors
        console.error('Error initializing progress:', error);
        return false;
      }
      return true;
    } catch (err) {
      console.error('Error in initializeModuleProgress:', err);
      return false;
    }
  }

  /**
   * Update module progress (0-100)
   */
  static async updateModuleProgress(
    userId: string,
    moduleId: string,
    courseId: string,
    progress: number
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_module_progress')
        .upsert({
          user_id: userId,
          module_id: moduleId,
          course_id: courseId,
          progress: Math.min(100, Math.max(0, progress)),
          completed_at: progress === 100 ? new Date().toISOString() : null
        })
        .eq('user_id', userId)
        .eq('module_id', moduleId);
      
      if (error) {
        console.error('Error updating progress:', error);
        return false;
      }
      return true;
    } catch (err) {
      console.error('Error in updateModuleProgress:', err);
      return false;
    }
  }

  /**
   * Get course progress summary
   */
  static async getCourseProgress(
    userId: string,
    courseId: string
  ): Promise<CourseProgressSummary> {
    try {
      // Get all modules for the course
      const { data: courseModules } = await supabase
        .from('course_modules')
        .select('module_id')
        .eq('course_id', courseId)
        .order('module_order');
      
      const totalModules = courseModules?.length || 0;
      
      // Get user progress for this course
      const { data: userProgress } = await supabase
        .from('user_module_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('course_id', courseId);
      
      const completedModules = userProgress?.filter(p => p.progress === 100).length || 0;
      const avgProgress = userProgress?.length 
        ? userProgress.reduce((sum, p) => sum + p.progress, 0) / totalModules
        : 0;
      
      return {
        totalModules,
        completedModules,
        overallProgress: Math.round(avgProgress),
        isCompleted: completedModules === totalModules && totalModules > 0
      };
    } catch (err) {
      console.error('Error in getCourseProgress:', err);
      return {
        totalModules: 0,
        completedModules: 0,
        overallProgress: 0,
        isCompleted: false
      };
    }
  }

  /**
   * Get progress for multiple courses (batch operation)
   */
  static async getMultipleCourseProgress(
    userId: string,
    courseIds: string[]
  ): Promise<Map<string, number>> {
    const progressMap = new Map<string, number>();
    
    try {
      const results = await Promise.all(
        courseIds.map(courseId => this.getCourseProgress(userId, courseId))
      );
      
      courseIds.forEach((courseId, index) => {
        progressMap.set(courseId, results[index].overallProgress);
      });
    } catch (err) {
      console.error('Error in getMultipleCourseProgress:', err);
    }
    
    return progressMap;
  }
}
